import {
  isKanchatMetricsEnabled,
  recordRequest,
} from '@/modules/core/metrics/kanchatMetrics';

/**
 * Times every API call made through services/_utils Fetch and reports the slow or
 * failed ones as `api_fetch_slow`, split into where the time went. Each call is
 * matched to the browser's own Resource Timing entry by exact URL and start time
 * (fetch() is called synchronously right after we read the clock), so there is no
 * guessing about which network request belongs to which call.
 *
 * Phases need the API to send Timing-Allow-Origin, and server_ms needs Server-Timing
 * `app` (suppliers-api sends both). Without them only total_ms is reported.
 */
export const SLOW_FETCH_MS = 1000;

// Entries for a call start within this of our clock read (measured: ~0.2ms).
const START_MATCH_MS = 2;
// A response body may finish arriving long after the headers; give up after this.
const ENTRY_WAIT_MS = 30_000;

type Track = (properties: Record<string, unknown>) => void;

type Pending = {
  href: string;
  method: string;
  status: number;
  startedAt: number;
  resumedAt: number;
  timer: ReturnType<typeof setTimeout>;
};

let track: Track | undefined;
let recent: PerformanceResourceTiming[] = [];
let pending: Pending[] = [];

// Path only, and any segment with a digit becomes :id: query strings and segments can
// carry phones, ids or search text.
export const endpointName = (href: string) =>
  new URL(href).pathname
    .split('/')
    .map((segment) => (/\d/.test(segment) ? ':id' : segment))
    .join('/');

const report = (call: Pending, entry?: PerformanceResourceTiming) => {
  const properties: Record<string, unknown> = {
    endpoint: endpointName(call.href),
    method: call.method,
    status: call.status, // 0 = the request failed before any response
    total_ms: Math.round(call.resumedAt - call.startedAt),
  };
  // Phases are 0 unless the API sends Timing-Allow-Origin.
  if (entry && entry.responseStart > 0) {
    const server = entry.serverTiming?.find((t) => t.name === 'app');
    const connect = entry.connectEnd - entry.connectStart; // 0 on a reused connection
    Object.assign(properties, {
      // Waiting in the browser before the request went out, excluding its own connect
      // (e.g. queued behind a connection another call was still opening).
      queue_ms: Math.round(entry.requestStart - entry.startTime - connect),
      connect_ms: Math.round(connect),
      ttfb_ms: Math.round(entry.responseStart - entry.requestStart),
      server_ms: server ? Math.round(server.duration) : undefined,
      download_ms: Math.round(entry.responseEnd - entry.responseStart),
      // Response headers were in, but the page only got back to the caller this late.
      resolve_delay_ms: Math.max(
        0,
        Math.round(call.resumedAt - entry.responseStart)
      ),
    });
  }
  track?.(properties);
};

const settle = () => {
  pending = pending.filter((call) => {
    const index = recent.findIndex(
      (e) =>
        e.name === call.href &&
        e.startTime >= call.startedAt &&
        e.startTime - call.startedAt <= START_MATCH_MS
    );
    if (index === -1) return true;
    const [entry] = recent.splice(index, 1);
    clearTimeout(call.timer);
    report(call, entry);
    return false;
  });
  // Unclaimed entries are fast calls; anything older than the wait can't match anymore.
  const cutoff = performance.now() - ENTRY_WAIT_MS;
  recent = recent.filter((e) => e.startTime >= cutoff);
};

/** Starts reporting slow API calls; call once with where events should go. */
export function trackSlowFetches(apiUrl: string | undefined, sink: Track) {
  if (
    !apiUrl ||
    typeof PerformanceObserver === 'undefined' ||
    !PerformanceObserver.supportedEntryTypes?.includes('resource')
  ) {
    return;
  }
  try {
    const prefix = new URL(apiUrl).href;
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
        if (entry.initiatorType === 'fetch' && entry.name.startsWith(prefix)) {
          recent.push(entry);
        }
      }
      settle();
    }).observe({ type: 'resource' });
    track = sink;
  } catch {
    // Analytics must never break the app; without it nothing is timed.
  }
}

/**
 * Call right before fetch(url); call the returned function as soon as fetch resolves
 * (with the status) or rejects (with 0). Never throws into the request path.
 */
export function timeFetch(url: string, method = 'GET') {
  if (!track && !isKanchatMetricsEnabled) return () => {};
  const startedAt = performance.now();
  return (status: number) => {
    try {
      const resumedAt = performance.now();
      if (isKanchatMetricsEnabled) {
        recordRequest({
          method,
          url,
          status,
          durationMs: resumedAt - startedAt,
          error: status === 0,
        });
      }
      if (!track) return;
      const slow = resumedAt - startedAt >= SLOW_FETCH_MS;
      if (!slow && status !== 0 && status < 500) return;
      const call: Pending = {
        href: new URL(url).href,
        method: method.toUpperCase(),
        status,
        startedAt,
        resumedAt,
        timer: setTimeout(() => {
          pending = pending.filter((p) => p !== call);
          report(call);
        }, ENTRY_WAIT_MS),
      };
      pending.push(call);
      settle();
    } catch {
      // Analytics must never break an API call.
    }
  };
}
